"use client";

import React, { useRef, useState } from 'react';
import {
  IoStar,
  IoChevronBack,
  IoChevronForward,
  IoCheckmarkCircle,
  IoShieldCheckmark,
  IoSparkles
} from 'react-icons/io5';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { ProductReview } from '@/types';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

export interface ReviewsCarouselProps {
  reviews: ProductReview[];
  averageRating?: number;
  totalReviews?: number;
  title?: string;
  subtitle?: string;
  badgeText?: string;
  containerId?: string;
  className?: string;
  variant?: 'light' | 'warm';
}

const AVATAR_BG_PALETTES = [
  'bg-amber-100 text-amber-900 border-amber-200/70',
  'bg-orange-100 text-orange-900 border-orange-200/70',
  'bg-rose-100 text-rose-900 border-rose-200/70',
  'bg-yellow-100 text-yellow-900 border-yellow-200/70',
  'bg-stone-100 text-stone-900 border-stone-200/70',
];

function getAvatarPalette(name: string): string {
  if (!name) return AVATAR_BG_PALETTES[0];
  const charCode = name.charCodeAt(0) + (name.charCodeAt(name.length - 1) || 0);
  return AVATAR_BG_PALETTES[charCode % AVATAR_BG_PALETTES.length];
}

export default function ReviewsCarousel({
  reviews = [],
  averageRating = 4.8,
  totalReviews,
  title = "Verified Buyer Reviews",
  subtitle,
  badgeText = "Customer Feedback",
  containerId = "reviews",
  className = "",
  variant = 'warm'
}: ReviewsCarouselProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const displayCount = totalReviews || reviews.length;
  const ratingValue = Number(averageRating) || 4.8;

  if (!reviews || reviews.length === 0) {
    return (
      <section id={containerId} className={`py-12 ${className}`}>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="rounded-3xl border border-dashed border-amber-900/15 bg-[#FFFDF9]/60 p-10">
            <IoSparkles className="mx-auto text-3xl text-amber-500/70 mb-3" />
            <h3 className="font-serif text-xl font-bold text-stone-800 mb-1">{title}</h3>
            <p className="text-sm text-stone-500">No written reviews yet. Be the first to share your experience!</p>
          </div>
        </div>
      </section>
    );
  }

  const isWarm = variant === 'warm';

  return (
    <section
      id={containerId}
      className={`relative py-12 md:py-16 ${isWarm ? 'bg-[#FFFDF9]/80 border-y border-orange-100/70' : 'bg-white'} ${className}`}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 sm:mb-10">
          <div>
            {badgeText && (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-800 text-xs font-bold uppercase tracking-wider mb-2.5">
                <IoStar className="text-amber-500 text-xs" />
                <span>{badgeText}</span>
              </div>
            )}
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-stone-900 tracking-tight">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-1.5 text-xs sm:text-sm text-stone-600 max-w-xl">
                {subtitle}
              </p>
            )}
          </div>

          {/* Rating Summary & Carousel Navigation Controls */}
          <div className="flex items-center justify-between sm:justify-end gap-6 pt-2 sm:pt-0 border-t sm:border-t-0 border-stone-200/60">
            {/* Rating pill */}
            <div className="flex items-center gap-3">
              <div className="font-serif text-3xl sm:text-4xl font-black text-stone-900 leading-none">
                {ratingValue.toFixed(1)}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }, (_, i) => {
                    const filled = i < Math.floor(ratingValue);
                    const half = !filled && i < ratingValue;
                    return (
                      <span key={i} className="relative inline-block text-amber-500">
                        <IoStar
                          className={filled || half ? 'text-amber-500' : 'text-stone-300'}
                          size={16}
                        />
                      </span>
                    );
                  })}
                </div>
                <p className="text-[11px] sm:text-xs text-stone-500 font-medium mt-0.5 whitespace-nowrap">
                  Based on {displayCount} verified review{displayCount === 1 ? '' : 's'}
                </p>
              </div>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-2">
              <button
                ref={prevRef}
                disabled={isBeginning}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 shadow-sm transition-all duration-200 hover:border-amber-400 hover:bg-amber-50/80 hover:text-amber-900 disabled:opacity-35 disabled:hover:bg-white disabled:hover:border-stone-200 disabled:hover:text-stone-700 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                aria-label="Previous review"
              >
                <IoChevronBack size={18} />
              </button>
              <button
                ref={nextRef}
                disabled={isEnd}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-700 shadow-sm transition-all duration-200 hover:border-amber-400 hover:bg-amber-50/80 hover:text-amber-900 disabled:opacity-35 disabled:hover:bg-white disabled:hover:border-stone-200 disabled:hover:text-stone-700 disabled:cursor-not-allowed cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                aria-label="Next review"
              >
                <IoChevronForward size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Carousel Slider */}
        <div className="relative -mx-2 px-2 pb-2">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={16}
            slidesPerView={1.15}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              if (typeof swiper.params.navigation !== 'boolean') {
                const nav = swiper.params.navigation;
                if (nav) {
                  nav.prevEl = prevRef.current;
                  nav.nextEl = nextRef.current;
                }
              }
            }}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            breakpoints={{
              480: {
                slidesPerView: 1.35,
                spaceBetween: 16,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 18,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 22,
              },
            }}
            className="!pb-10 !pt-2"
          >
            {reviews.map((rev, idx) => {
              const userName = rev.userName || 'Verified Buyer';
              const initial = userName.trim().charAt(0).toUpperCase() || 'V';
              const palette = getAvatarPalette(userName);
              const starCount = Math.max(1, Math.min(5, Math.round(Number(rev.rating) || 5)));

              return (
                <SwiperSlide key={`${userName}-${idx}`} className="!h-auto">
                  <div className="group relative flex h-full flex-col justify-between rounded-2xl sm:rounded-3xl border border-orange-100/90 bg-gradient-to-b from-white via-[#FFFDF9] to-[#FFFBF4] p-5 sm:p-6 shadow-[0_4px_20px_-4px_rgba(251,191,36,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-amber-400/50 hover:shadow-[0_12px_28px_-6px_rgba(251,191,36,0.18)]">
                    {/* Top row: Star Rating + Date */}
                    <div>
                      <div className="flex items-center justify-between mb-3.5">
                        <div className="flex items-center gap-1">
                          {Array.from({ length: 5 }, (_, i) => (
                            <IoStar
                              key={i}
                              className={i < starCount ? 'text-amber-500' : 'text-stone-200'}
                              size={15}
                            />
                          ))}
                        </div>
                        {rev.createdAt && (
                          <span className="text-[11px] font-medium text-stone-400">
                            {rev.createdAt}
                          </span>
                        )}
                      </div>

                      {/* Review Quote Body */}
                      <p className="text-xs sm:text-sm leading-relaxed text-stone-700 font-normal line-clamp-5 relative">
                        &ldquo;{rev.review}&rdquo;
                      </p>
                    </div>

                    {/* Bottom row: Reviewer info & Verified Badge */}
                    <div className="mt-5 flex items-center justify-between gap-3 pt-3.5 border-t border-orange-100/70">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div
                          className={`w-8 h-8 rounded-full border flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-sm ${palette}`}
                        >
                          {initial}
                        </div>
                        <span className="text-xs sm:text-sm font-bold text-stone-900 truncate">
                          {userName}
                        </span>
                      </div>

                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold text-emerald-700 border border-emerald-200/60 flex-shrink-0">
                        <IoCheckmarkCircle className="text-emerald-600 text-xs" />
                        <span>Verified</span>
                      </span>
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
